import React, { useEffect, useState } from "react"
import { Form, Select } from "antd"
import PropTypes from "prop-types"
import { CustomTimeDropdown } from "../CustomTimeDropdown"
import { useTranslation } from "react-i18next"

const { Option } = Select

const predefinedOptions = [
  { label: "15 min", value: 15 },
  { label: "30 min", value: 30 },
  { label: "45 min", value: 45 },
  { label: "1 hr", value: 60 }
]

export const BufferTime = ({ form, expandedRowData }) => {
    const { t } = useTranslation()
  const [value, setValue] = useState(null)
  const [customHour, setCustomHour] = useState(0)
  const [customMinute, setCustomMinute] = useState(0)
  const [error, setError] = useState("")
  const [open, setOpen] = useState(false)
  const availableHours = form.getFieldValue("available_hours")
  // Watch (not getFieldValue) so the custom-buffer inputs enable and the max-buffer cap
  // recalculates the moment a Duration is selected — a one-time read left them disabled.
  const duration = Form.useWatch("trip_duration", form) || 0

  // Get all available time slots from availableHours
  const getAvailableTimeSlots = (availableHours) => {
    if (!Array.isArray(availableHours) || availableHours.length === 0) return []
    return availableHours
      .map((b) => {
        if (!b?.start || !b?.end) return null
        const start = typeof b.start === "string" && window.dayjs ? window.dayjs(b.start) : b.start
        const end = typeof b.end === "string" && window.dayjs ? window.dayjs(b.end) : b.end
        if (!start || !end || !start.isValid?.() || !end.isValid?.()) return null
        const diff = end.diff(start, "minute")
        return diff > 0 ? diff : null
      })
      .filter(Boolean)
  }

  // Calculate maximum allowed buffer time based on selected duration and available time slots
  const calculateMaxBufferTime = () => {
    const timeSlots = getAvailableTimeSlots(availableHours)
    if (timeSlots.length === 0 || !duration) return null

    // Find the smallest time slot that can accommodate the selected duration
    const validSlots = timeSlots.filter((slot) => slot >= duration)
    if (validSlots.length === 0) return 0 // No valid slots

    // The maximum buffer is the difference between the slot and selected duration
    const maxBuffer = Math.min(...validSlots) - duration
    return maxBuffer > 0 ? maxBuffer : 0
  }

  useEffect(() => {
    const existingValue = expandedRowData?.trip_buffer_time
    if (existingValue != null) {
      setValue(existingValue)
      form.setFieldValue("trip_buffer_time", existingValue)
      const isPredefined = predefinedOptions.some((opt) => opt.value === existingValue)
      if (!isPredefined && !isNaN(existingValue)) {
        setCustomHour(Math.floor(existingValue / 60))
        setCustomMinute(existingValue % 60)
      }
    } else {
      form.setFieldValue("trip_buffer_time", null)
      setValue(null)
      setCustomHour(0)
      setCustomMinute(0)
    }
  }, [form, expandedRowData])

  const getDropdownRender = (menu) => (
    <CustomTimeDropdown
      menu={menu}
      customHour={customHour}
      customMinute={customMinute}
      error={error}
      setCustomHour={setCustomHour}
      setCustomMinute={setCustomMinute}
      handleCustomChange={handleCustomChange}
      label="Enter Custom Buffer Time"
      disabled={duration === 0}
    />
  )

  const handleSelect = (val) => {
    setValue(val)
    form.setFieldValue("trip_buffer_time", val)
    setError("")
    setOpen(false)
  }

  const handleCustomChange = () => {
    const minuteValue = Number(customMinute) || 0
    const hourValue = Number(customHour) || 0
    const totalBuffer = hourValue * 60 + minuteValue
    const maxBuffer = calculateMaxBufferTime()

    // Basic minute validation
    if (minuteValue < 0 || minuteValue > 59) {
      setError(t("experiences.scheduling.errors.minute_limit"))
      return
    }

    // Buffer time validation
    if (maxBuffer !== null && totalBuffer > maxBuffer) {
      setError(t("experiences.scheduling.errors.buffer_time_limit"))
      return
    }

    setValue(totalBuffer)
    form.setFieldValue("trip_buffer_time", totalBuffer)
    setError("")
    setOpen(false)
  }

  const timeSlots = getAvailableTimeSlots(availableHours)
  const maxBuffer = calculateMaxBufferTime()

  return (
    <div>
      <Select
        open={open}
        onDropdownVisibleChange={(visible) => setOpen(visible)}
        style={{ width: "100%" }}
        placeholder="Select"
        value={
          predefinedOptions.find((opt) => opt.value === value)?.label ||
          (value ? `${Math.floor(value / 60)} hr ${value % 60} min` : undefined)
        }
        onSelect={handleSelect}
        dropdownRender={getDropdownRender}
      >
        {predefinedOptions?.map((opt) => {
          return (
            <Option
              key={opt.value}
              value={opt.value}
              disabled={timeSlots.length > 0 && opt.value > maxBuffer}
            >
              {opt.label}
            </Option>
          )
        })}
      </Select>
    </div>
  )
}

BufferTime.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func.isRequired,
    setFieldValue: PropTypes.func.isRequired
  }).isRequired,
  expandedRowData: PropTypes.shape({
    trip_buffer_time: PropTypes.number
  })
}
